/**
 * 取得のお知らせ（#225 ★4、docs/91 §3.7）：送ったメールの閲覧ページを受け取った人が開いた・ダウンロードしたら、送った人の画面の右下に通知（docs/67 の仕組み）。
 *   ログイン中の画面（Shell）が 1分ごとに GET /mail-notices を見に行き（画面が見えているときだけ）、出したら POST /mail-notices/seen（1つの送付で1回）。
 *   どちらも人の操作ではない＝PASSIVE（自動ログアウトの操作に数えない・利用状況に数えない＝API の core/usage.ts の PASSIVE）
 */
import { MAIL_MSG, type MailNotices } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api } from "../api.js";
import { PASSIVE } from "../lib/autoLogout.js";
import { notify } from "./Notice.jsx";

export const MAIL_NOTICE_POLL_MS = 60_000;

export function useMailNotices(enabled = true) {
  const shown = useRef(new Set<string>());
  const q = useQuery({
    queryKey: ["mail-notices"],
    queryFn: () => api<MailNotices>("GET", "/mail-notices", undefined, PASSIVE),
    enabled,
    retry: false,
    refetchInterval: enabled ? MAIL_NOTICE_POLL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  useEffect(() => {
    const items = (q.data?.items ?? []).filter((x) => !shown.current.has(x.deliveryId));
    if (!items.length) return;
    for (const x of items) {
      shown.current.add(x.deliveryId);
      notify({ text: MAIL_MSG.noticeOpened.replace("{to}", x.to).replace("{docs}", x.docs), tone: "info", key: `mail-opened-${x.deliveryId}`, testId: "mail-opened-notice" });
    }
    void api("POST", "/mail-notices/seen", { ids: items.map((x) => x.deliveryId) }, PASSIVE).catch(() => undefined);
  }, [q.data]);
}

/**
 * {orderAck}・紙で受け取りたい のお知らせ（#233、docs/93 §4.4）：{vendor}が承諾ページで「承諾」・「紙で受け取りたい」を押したら、鍵を作った人（発行した人）の画面の右下に。
 * 同じく 1分ごと（PASSIVE）・出したら POST /order-notices/seen。sales の会社だけ（API の門と同じ）
 */
export function useOrderNotices(enabled = true) {
  const qc = useQueryClient();
  const shown = useRef(new Set<string>());
  const q = useQuery({
    queryKey: ["order-notices"],
    queryFn: () => api<{ items: Array<{ id: string; documentId: string; kind: "accepted" | "declined"; text: string }> }>("GET", "/order-notices", undefined, PASSIVE),
    enabled,
    retry: false,
    refetchInterval: enabled ? MAIL_NOTICE_POLL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  useEffect(() => {
    const items = (q.data?.items ?? []).filter((x) => !shown.current.has(x.id));
    if (!items.length) return;
    for (const x of items) {
      shown.current.add(x.id);
      notify({ text: x.text, tone: x.kind === "accepted" ? "success" : "info", key: `order-${x.kind}-${x.id}`, testId: `order-${x.kind}-notice` });
      // 開いている{order}の画面（チップ・承諾の記録・送信方法）も読み直す（通知だけ出て画面が「承諾待ち」のまま残らないように）
      void qc.invalidateQueries({ queryKey: ["order", x.documentId] });
    }
    void qc.invalidateQueries({ queryKey: ["orders"] });
    void api("POST", "/order-notices/seen", { ids: items.map((x) => x.id) }, PASSIVE).catch(() => undefined);
  }, [q.data]);
}

/**
 * {paymentNotice}の確認・誤りがある のお知らせ（#233 3b、docs/93 §15.6）：{vendor}が確認ページで「確認」・「誤りがある」を押したら、鍵を作った人（発行した人）の画面の右下に。
 * 同じく 1分ごと（PASSIVE）・出したら POST /payment-notice-notices/seen。sales の会社だけ（API の門と同じ）。誤りがあるは朱（消えない誤りの通知）
 */
export function usePaymentNoticeNotices(enabled = true) {
  const qc = useQueryClient();
  const shown = useRef(new Set<string>());
  const q = useQuery({
    queryKey: ["payment-notice-notices"],
    queryFn: () => api<{ items: Array<{ id: string; documentId: string; kind: "confirmed" | "disputed"; text: string }> }>("GET", "/payment-notice-notices", undefined, PASSIVE),
    enabled,
    retry: false,
    refetchInterval: enabled ? MAIL_NOTICE_POLL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  useEffect(() => {
    const items = (q.data?.items ?? []).filter((x) => !shown.current.has(x.id));
    if (!items.length) return;
    for (const x of items) {
      shown.current.add(x.id);
      notify({ text: x.text, tone: x.kind === "confirmed" ? "success" : "error", key: `notice-${x.kind}-${x.id}`, testId: `notice-${x.kind}-notice` });
      void qc.invalidateQueries({ queryKey: ["payment-notice", x.documentId] });
    }
    void qc.invalidateQueries({ queryKey: ["payment-notices"] });
    void qc.invalidateQueries({ queryKey: ["purchases"] });
    void api("POST", "/payment-notice-notices/seen", { ids: items.map((x) => x.id) }, PASSIVE).catch(() => undefined);
  }, [q.data]);
}
